import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import './RunPayrollPage.css';

const RunPayrollPage = () => {
    const { id: businessId } = useParams();
    const navigate = useNavigate();
    const [hourlyEmployees, setHourlyEmployees] = useState([]);
    const [salariedEmployees, setSalariedEmployees] = useState([]);
    const [payPeriod, setPayPeriod] = useState(null);
    const [employeeInputs, setEmployeeInputs] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const frequencyMultiplier = {
        weekly: 52,
        biweekly: 26,
        semimonthly: 24,
        monthly: 12,
    };

    const getPayPeriodSalary = (annualSalary) => {
        if (!payPeriod || !payPeriod.frequency) return annualSalary;
        const multiplier = frequencyMultiplier[payPeriod.frequency.toLowerCase()] || 1;
        const frequencySalary = (annualSalary / multiplier).toFixed(2);
        return frequencySalary;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const payPeriodResponse = await api.get(`/business/${businessId}/pay-period`);
                setPayPeriod(payPeriodResponse.data);

                const hourlyResponse = await api.get(`/businesses/${businessId}/employees/hourly`);
                const hourly = hourlyResponse.data.map((employee) => ({
                    ...employee,
                    type: "hourly",
                }));
                setHourlyEmployees(hourly);

                const salariedResponse = await api.get(`/businesses/${businessId}/employees/salary`);
                const salaried = salariedResponse.data.map((employee) => ({
                    ...employee,
                    type: "salaried",
                }));
                setSalariedEmployees(salaried);

                const initialInputs = {};
                [...hourly, ...salaried].forEach((employee) => {
                    initialInputs[employee.id] = {
                        regularHours: null,
                        overtimeHours: null,
                        doubletimeHours: null,
                        cashAdvance: null,
                        bonus: null,
                        tips: null,
                        reimbursement: null,
                        other: null,
                        vacationHours: employee.type === "hourly" ? null : null,
                        vacationPay: employee.type === "salaried" ? null : null,
                    };
                });
                setEmployeeInputs(initialInputs);

                setError(null);
            } catch (err) {
                console.error("Error fetching employees:", err);
                setError("Failed to fetch employees. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [businessId]);

    const handleInputChange = (employeeId, field, value) => {
        setEmployeeInputs((prev) => ({
            ...prev,
            [employeeId]: {
                ...prev[employeeId],
                [field]: value ? parseFloat(value) : null,
            },
        }));
    };


    


    const handleSubmit = async () => {
        const collectedResults = [...hourlyEmployees, ...salariedEmployees].map((employee) => {
            const perhourSalary = employee.salary_amt / 52 / 40 || null;
            const employeeInput = employeeInputs[employee.id] || {}; // Ensure employeeInput is always defined

            const extraFields = {
                cashAdvance: employeeInput.cashAdvance || 0,
                bonus: employeeInput.bonus || 0,
                tips: employeeInput.tips || 0,
                reimbursement: employeeInput.reimbursement || 0,
                other: employeeInput.other || 0,
            };
    
            const commonFields = {
                id: employee.id,
                business_id: businessId,
                business_state: payPeriod.b_state,
                start_date: payPeriod.start_date,
                end_date: payPeriod.end_date,
                pay_date: payPeriod.pay_date,
                frequency: payPeriod.frequency,
                name: `${employee.first_name} ${employee.last_name}`,
                perhour_salary: perhourSalary,
            };
    
            if (employee.type === "hourly") {
                return {
                    ...commonFields,
                    ...extraFields, 
                    hourly_rate: employee.hourly_amt,
                    regularHours: employeeInput.regularHours !== null && employeeInput.regularHours !== undefined ? employeeInput.regularHours : 0, // Set default to 0
                    overtimeHours: employeeInput.overtimeHours || 0,
                    doubletimeHours: employeeInput.doubletimeHours || 0,
                    vacationHours: employeeInput.vacationHours || 0,
                };
            } else {
                return {
                    ...commonFields,
                    ...extraFields, 
                    salary_amt: Number(getPayPeriodSalary(employee.salary_amt)),
                    vacationPay: employeeInput.vacationPay || 0,
                };
            }
        });
    

    
        try {
            const response = await api.post(`/payroll/new-business`, { payrollData: collectedResults }); // Ensure payload matches API expectation
            const { success, errors } = response.data;
    
            if (errors.length > 0) {
                alert(
                    `Partial success:\n\nSuccess:\n${success
                        .map((item) => ` - ${item.name}`)
                        .join("\n")}\n\nErrors:\n${errors.join("\n")}`
                );
            } else {
                alert("All payroll records processed successfully.");
                navigate(`/dashboard/business/${businessId}/viewpayroll`);
            }
        } catch (error) {
            console.error("Error submitting payroll data:", error);
            alert("Failed to submit payroll data. Please try again.");
        }
    };
    

    if (loading) {
        return <p>Loading employees...</p>;
    }

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div className="run-body">
        <h2>New Payroll</h2>
        <div>
            {payPeriod && (
                <div className="pay-period-payroll-cont">
                <div className="ppl-pay">
                    <label>Pay Period:</label> 
                    <span className="pay-period-content">
                    {new Date(payPeriod.start_date).toLocaleDateString()} -{" "}
                    {new Date(payPeriod.end_date).toLocaleDateString()}
                    </span>
                    </div>
                <div className="ppl-pay">
                    <label>Pay Date:</label>{" "}
                    <span className="pay-period-content">
                    {new Date(payPeriod.pay_date).toLocaleDateString()}
                    </span>
                    </div>
                </div>
            )}

            <table className="run-table">
                
                <tbody className="run-tbody">
                    {[...hourlyEmployees, ...salariedEmployees].map((employee) => (
                        <tr className="run-row" key={employee.id}>
                            <td>{employee.first_name} {employee.last_name}</td>
                            <td>{employee.type === "hourly" ? "Hourly" : "Salaried"}</td>
                            <td>
                                {employee.type === "hourly" 
                                    ? `$${employee.hourly_amt} per hour` 
                                    : `$${getPayPeriodSalary(employee.salary_amt)} per period`}
                            </td>
                            <td>
                                {employee.type === "hourly" && (
                                    <>
                                        <input
                                            type="number"
                                            placeholder="Regular Hours"
                                            value={employeeInputs[employee.id]?.regularHours ?? ""}
                                            onChange={(e) =>
                                                handleInputChange(employee.id, "regularHours", e.target.value)
                                            }
                                        />
                                        <input
                                            type="number"
                                            placeholder="Overtime Hours"
                                            value={employeeInputs[employee.id]?.overtimeHours ?? ""}
                                            onChange={(e) =>
                                                handleInputChange(employee.id, "overtimeHours", e.target.value)
                                            }
                                        />
                                        <input
                                            type="number"
                                            placeholder="Doubletime Hours"
                                            value={employeeInputs[employee.id]?.doubletimeHours ?? ""}
                                            onChange={(e) =>
                                                handleInputChange(employee.id, "doubletimeHours", e.target.value)
                                            }
                                        />
                                        <input
                                            type="number"
                                            placeholder="Vacation Hours"
                                            value={employeeInputs[employee.id]?.vacationHours ?? ""}
                                            onChange={(e) =>
                                                handleInputChange(employee.id, "vacationHours", e.target.value)
                                            }
                                        />
                                    </>
                                )}
                                {employee.type === "salaried" && (
                                    <input
                                        type="number"
                                        placeholder="Vacation Pay"
                                        value={employeeInputs[employee.id]?.vacationPay ?? ""}
                                        onChange={(e) =>
                                            handleInputChange(employee.id, "vacationPay", e.target.value)
                                        }
                                    />
                                )}
                                <input
                                    type="number"
                                    placeholder="Cash Advance"
                                    value={employeeInputs[employee.id]?.cashAdvance ?? ""}
                                    onChange={(e) =>
                                        handleInputChange(employee.id, "cashAdvance", e.target.value)
                                    }
                                />

                                <input
                                    type="number"
                                    placeholder="Bonus"
                                    value={employeeInputs[employee.id]?.bonus ?? ""}
                                    onChange={(e) => handleInputChange(employee.id, "bonus", e.target.value)}
                                />
                                <input
                                    type="number"
                                    placeholder="Tips"
                                    value={employeeInputs[employee.id]?.tips ?? ""}
                                    onChange={(e) => handleInputChange(employee.id, "tips", e.target.value)}
                                />
                                <input
                                    type="number"
                                    placeholder="Reimbursement"
                                    value={employeeInputs[employee.id]?.reimbursement ?? ""}
                                    onChange={(e) =>
                                        handleInputChange(employee.id, "reimbursement", e.target.value)
                                    }
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="button-container">
            <button onClick={handleSubmit}>Submit</button>
            </div>
            
        </div>
        </div>
    );
};

export default RunPayrollPage; 





